import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Heading,
  Loading,
  Row,
  Screen,
  TextField,
  Title,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import { formatPhone, waMeLink } from '../lib/format';
import type { CustomerSummary } from './Customers';
import './book.css';

/**
 * Make a booking (§5, §7).
 *
 * Customer + cart + time + "wants to see". No consultant is chosen — whoever
 * signs in on the cart takes the call.
 */

interface Slot {
  label: string;
  start_at: string;
  end_at: string;
  available: boolean;
  reason: string | null;
}

interface SlotsResponse {
  cart: { id: string; name: string };
  date: string;
  shop_tz: string;
  duration_min: number;
  buffer_min: number;
  call_hours: string;
  slots: Slot[];
}

interface Created {
  booking: {
    id: string;
    start_at: string;
    duration_min: number;
    wants_to_see: string | null;
    cart: { id: string; name: string };
    customer: { id: string; name: string; phone: string; timezone: string | null };
  };
  nyauta_token: string;
  shop_tz: string;
}

/** Today in the shop's zone, so the date box opens on the right day. */
function shopToday(tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export default function Book() {
  const [params] = useSearchParams();
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  const [carts, setCarts] = useState<{ id: string; name: string }[] | null>(null);
  const [cartId, setCartId] = useState('');
  const [date, setDate] = useState(shopToday('Asia/Kolkata'));
  const [wantsToSee, setWantsToSee] = useState('');
  const [slots, setSlots] = useState<SlotsResponse | null>(null);
  const [chosen, setChosen] = useState<Slot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);

  // Pre-filled from a customer record's "New Nyauta".
  const presetCustomer = params.get('customer');

  useEffect(() => {
    void (async () => {
      try {
        const res = await api<{ carts: { id: string; name: string; active: boolean }[] }>('/api/bookings/carts');
        const active = res.carts.filter((c) => c.active);
        setCarts(active);
        if (active.length && !cartId) setCartId(active[0].id);
      } catch {
        setCarts([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!presetCustomer) return;
    void (async () => {
      try {
        const res = await api<{ customer: CustomerSummary }>(`/api/customers/${presetCustomer}`);
        setCustomer(res.customer);
      } catch {
        /* the picker below still works */
      }
    })();
  }, [presetCustomer]);

  const loadSlots = useCallback(async () => {
    if (!cartId || !date) return;
    setSlots(null);
    setError(null);
    try {
      setSlots(
        await api<SlotsResponse>(`/api/bookings/slots?cart_id=${encodeURIComponent(cartId)}&date=${date}`)
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the times.');
    }
  }, [cartId, date]);

  useEffect(() => {
    void loadSlots();
  }, [loadSlots]);

  async function create() {
    if (busy || !customer || !chosen) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<Created>('/api/bookings', {
        method: 'POST',
        body: {
          customer_id: customer.id,
          cart_id: cartId,
          start_at: chosen.start_at,
          wants_to_see: wantsToSee || null,
        },
      });
      setCreated(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not make that booking.');
      // A lost race means the grid is stale — refresh it so the taken slot shows.
      void loadSlots();
      setChosen(null);
    } finally {
      setBusy(false);
    }
  }

  if (created) return <Done created={created} />;

  return (
    <Screen width="wide">
      <Title sub="New appointment">Mulaqaat</Title>

      <Row className="crumbs">
        <Link to="/" className="btn btn--ghost">
          ← Back
        </Link>
      </Row>

      <Card>
        <Heading>Who is it for?</Heading>
        {customer ? (
          <Row className="book__chosen">
            <div>
              <strong>{customer.name}</strong>
              <span className="book__sub">{formatPhone(customer.phone)}</span>
            </div>
            <Button variant="ghost" onClick={() => setCustomer(null)}>
              Change
            </Button>
          </Row>
        ) : (
          <CustomerPicker onPick={setCustomer} />
        )}
      </Card>

      <Card>
        <Heading>When?</Heading>

        <div className="book__controls">
          <div className="field">
            <label className="field__label" htmlFor="cart">
              Cart
            </label>
            <select id="cart" className="field__input" value={cartId} onChange={(e) => setCartId(e.target.value)}>
              {(carts ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <TextField
            label="Day"
            type="date"
            value={date}
            min={shopToday('Asia/Kolkata')}
            onChange={(e) => {
              setDate(e.target.value);
              setChosen(null);
            }}
          />
        </div>

        {error && !slots ? (
          <ErrorState message={error} onRetry={() => void loadSlots()} />
        ) : !slots ? (
          <Loading label="Loading times…" />
        ) : slots.slots.length === 0 ? (
          <EmptyState line="No times on this day." />
        ) : (
          <>
            <p className="book__hint">
              Shop time · {slots.call_hours} · {slots.duration_min} minutes each ·{' '}
              {slots.buffer_min} minutes between calls
            </p>
            <div className="slotgrid">
              {slots.slots.map((s) => (
                <button
                  key={s.start_at}
                  type="button"
                  className={`slot ${!s.available ? 'slot--out' : ''} ${
                    chosen?.start_at === s.start_at ? 'slot--on' : ''
                  }`}
                  disabled={!s.available}
                  onClick={() => setChosen(s)}
                  title={
                    s.reason === 'busy'
                      ? 'This cart is busy then'
                      : s.reason === 'past'
                        ? 'Already gone'
                        : undefined
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
          </>
        )}
      </Card>

      <Card>
        <Heading>What do they want to see?</Heading>
        <TextField
          label="Optional note"
          value={wantsToSee}
          onChange={(e) => setWantsToSee(e.target.value)}
          placeholder="Polki set for a wedding, budget around 2 lakh"
          maxLength={500}
        />

        {error && slots ? (
          <p className="field__error" role="alert">
            {error}
          </p>
        ) : null}

        <Row>
          <Button onClick={() => void create()} busy={busy} disabled={!customer || !chosen}>
            {busy ? 'Booking…' : 'Make the booking'}
          </Button>
          {chosen ? (
            <Chip tone="gold">
              {chosen.label} on {date}
            </Chip>
          ) : (
            <span className="book__sub">Pick a customer and a time.</span>
          )}
        </Row>
      </Card>
    </Screen>
  );
}

function CustomerPicker({ onPick }: { onPick: (c: CustomerSummary) => void }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<CustomerSummary[] | null>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await api<{ customers: CustomerSummary[] }>(
          `/api/customers?q=${encodeURIComponent(q)}&limit=20`
        );
        setRows(res.customers);
      } catch {
        setRows([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <>
      <TextField
        label="Find the customer"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Name or number"
        autoComplete="off"
      />
      {!rows ? (
        <Loading label="Searching…" />
      ) : rows.length === 0 ? (
        <EmptyState
          line="Nobody matches."
          action={
            <Link to="/customers" className="btn btn--secondary">
              Add a customer
            </Link>
          }
        />
      ) : (
        <ul className="picker__list">
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" className="picker__item" onClick={() => onPick(r)}>
                <span>{r.name}</span>
                <span className="picker__phone">{formatPhone(r.phone)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** After booking: the share step (§2 — v1 sends the link from the staff device). */
function Done({ created }: { created: Created }) {
  const { booking, nyauta_token, shop_tz } = created;
  const link = `${window.location.origin}/n/${nyauta_token}`;

  const shopWhen = new Intl.DateTimeFormat('en-GB', {
    timeZone: shop_tz,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(booking.start_at));

  const guestWhen = booking.customer.timezone
    ? new Intl.DateTimeFormat('en-GB', {
        timeZone: booking.customer.timezone,
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(new Date(booking.start_at))
    : null;

  // The guest's own clock leads, because that is the time they must remember.
  const message =
    `Sat Sri Akal ${booking.customer.name} ji 🙏\n\n` +
    `Arjun Jewellers ton tuhada Nyauta — a private video Mulaqaat.\n\n` +
    (guestWhen ? `${guestWhen} (your time)\n${shopWhen} India time\n\n` : `${shopWhen} India time\n\n`) +
    (booking.wants_to_see ? `For: ${booking.wants_to_see}\n\n` : '') +
    `Please save the date here:\n${link}`;

  return (
    <Screen>
      <Title sub="Booked">Mulaqaat</Title>

      <Card>
        <Row>
          <Chip tone="gold">Tentative</Chip>
          <span className="book__sub">Becomes a firm hold when they tap “Save the date”.</span>
        </Row>

        <Heading>{booking.customer.name}</Heading>
        <p className="book__when">{shopWhen} · shop time</p>
        {guestWhen ? <p className="book__when book__when--guest">{guestWhen} · their time</p> : null}
        {booking.wants_to_see ? <p className="book__sub">Wants to see: {booking.wants_to_see}</p> : null}

        <Row className="rec__actions">
          <a
            className="btn btn--primary"
            href={waMeLink(booking.customer.phone, message)}
            target="_blank"
            rel="noreferrer noopener"
          >
            Send the Nyauta on WhatsApp
          </a>
          <a className="btn btn--secondary" href={link} target="_blank" rel="noreferrer noopener">
            Preview the invitation
          </a>
        </Row>

        <p className="book__link">{link}</p>
      </Card>

      <Row className="crumbs">
        <Link to="/" className="btn btn--ghost">
          ← Done
        </Link>
        <Link to="/book" className="btn btn--secondary" onClick={() => window.location.reload()}>
          Book another
        </Link>
      </Row>
    </Screen>
  );
}
